import screenfull from "screenfull";
import { isSmartPhone } from "utility";
import videojs from "video.js";

import { blurFromActiveElement } from "./blurFromActiveElement";
import endFullscreenIcon from "./icon/endFullscreenIcon.svg";
import fullscreenIcon from "./icon/fullscreenIcon.svg";

import type Player from "video.js/dist/types/player";

type FullscreenElementOptions = {
  fullscreenElement?: HTMLDivElement;
};

export const getFullScreenModeIvsButton = () => {
  const fullscreenClassName = "fullscreen-mode-enabled";
  const VideoJSButton = videojs.getComponent("Button");

  class fullScreenModeButton extends VideoJSButton {
    constructor(player: Player, options = {}) {
      super(player, options);

      if (screenfull.isEnabled) {
        screenfull.on("change", this.syncFullscreenUI);
      }
      player.on("fullscreenchange", this.syncFullscreenUI);

      this.on("dispose", () => {
        if (screenfull.isEnabled) {
          screenfull.off("change", this.syncFullscreenUI);
        }
        player.off("fullscreenchange", this.syncFullscreenUI);
      });

      this.syncFullscreenUI();
    }

    isNowFullscreen = (): boolean => {
      if (isSmartPhone()) {
        return this.player()?.isFullscreen() ?? false;
      }

      const byDocument = document.fullscreenElement !== null;
      const byScreenfull = screenfull.isEnabled && screenfull.isFullscreen;
      const byPlayer = this.player().isFullscreen?.() ?? false;
      return byDocument || byScreenfull || byPlayer;
    };

    syncFullscreenUI = () => {
      const streamingEl = this.player().el();
      if (!streamingEl) return;

      const isFullscreen = this.isNowFullscreen();

      if (isFullscreen) {
        blurFromActiveElement();
      }

      if (isFullscreen) {
        streamingEl.classList.add(fullscreenClassName);
      } else {
        streamingEl.classList.remove(fullscreenClassName);
      }

      const btn = this.el();
      if (!btn) return;

      btn.innerHTML = isFullscreen
        ? `<img src="${endFullscreenIcon}" alt="Exit Fullscreen Button"/><span class="vjs-control-text">フルスクリーン解除</span>`
        : `<img src="${fullscreenIcon}" alt="Fullscreen Button"/><span class="vjs-control-text">フルスクリーンモード</span>`;
    };

    handleClick() {
      const options = this.options(undefined) as FullscreenElementOptions;

      if (this.isNowFullscreen()) {
        // --- フルスクリーンを解除 ---
        if (isSmartPhone()) {
          void this.player().exitFullscreen();
        } else if (screenfull.isEnabled) {
          void screenfull.exit();
        }
      } else {
        // --- フルスクリーンを開始 ---
        if (isSmartPhone()) {
          void this.player().requestFullscreen();
        } else if (screenfull.isEnabled && options.fullscreenElement) {
          void screenfull.request(options.fullscreenElement);
        }
      }
    }

    createEl() {
      return videojs.dom.createEl("div", {
        className:
          "vjs-custom-fullscreen-button vjs-custom-button vjs-control vjs-button",
        innerHTML: `<img src="${fullscreenIcon}" alt="Fullscreen Button"/><span class="vjs-control-text">フルスクリーンモード</span>`,
      });
    }
  }

  return fullScreenModeButton;
};
